import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    getInboundDocuments,
    dismissInboundDocument,
    getInboundDocumentFileBlob,
} from '../../lib/api';
import { formatDate, formatNumber } from '../../lib/format-utils';
import { getApiErrorMessage } from '../../lib/errors';
import { cn } from '../../lib/utils';
import { isAbandoned, buildIntakeHandoff } from './inbound-intake';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
} from '../../components/ui/alert-dialog';
import SupplierInvoicePreview from '../../components/supplier-invoice-preview';
import { toast } from 'sonner';
import {
    AlertTriangle,
    Check,
    ExternalLink,
    FileWarning,
    Inbox,
    Loader2,
    Mail,
    Receipt,
    RotateCw,
    Trash2,
} from 'lucide-react';

// The five statuses, kept separate on purpose (KD-090b): "we correctly skipped
// marketing" and "an invoice arrived that we could not read" demand different
// responses from a human and must never land in one pile.
const STATUS_OPTIONS = ['pending', 'error', 'ignored', 'confirmed', 'dismissed', 'all'];

// Which extracted fields are worth showing, in reading order. Absent keys are
// the NORMAL case — the extractor leaves a field out rather than guess
// (KD-090) — so a thin result is never an error state here.
const FIELD_ROWS = [
    ['invoice_number', 'supplier_invoices.invoice_number_label'],
    ['invoice_date', 'supplier_invoices.invoice_date_label'],
    ['due_date', 'supplier_invoices.due_date_label'],
    ['amount', 'supplier_invoices.amount_label'],
    ['vat_amount', 'supplier_invoices.vat_amount_label'],
    ['currency', 'supplier_invoices.currency_label'],
    ['payment_reference', 'supplier_invoices.payment_reference_label'],
    ['org_number', 'supplier_invoices.org_number_label'],
];

const MONEY_FIELDS = new Set(['amount', 'vat_amount']);
const DATE_FIELDS = new Set(['invoice_date', 'due_date']);

function formatFieldValue(key, value) {
    if (value === null || value === undefined || value === '') return null;
    if (MONEY_FIELDS.has(key)) return formatNumber(Number(value));
    if (DATE_FIELDS.has(key)) return formatDate(String(value));
    return String(value);
}

/**
 * The intake queue: mail that arrived at the invoice mailbox and is HELD until
 * a human turns it into a supplier invoice.
 *
 * WHY A HOLDING AREA EXISTS AT ALL (KD-090): a supplier invoice requires a
 * project, and a mail cannot know which one; and its doc_type is IMMUTABLE
 * once created. Both facts were discovered by reading the shipped model. So an
 * ingested mail cannot be a supplier invoice yet. A held document is only
 * ever confirmed as an INVOICE (KD-093): a receipt belongs in the
 * cardholder's expense report, so one guessed as a receipt gets a hint
 * saying so and is dismissed here.
 *
 * Confirming does NOT create anything here. It hands the document to the
 * existing Add dialog on SupplierInvoicesPage, with its own validation, its
 * project picker and its FX conversion — see buildIntakeHandoff.
 */
export default function InboundDocumentsPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [status, setStatus] = useState('pending');
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [attachmentIndex, setAttachmentIndex] = useState(0);
    const [dismissTarget, setDismissTarget] = useState(null);
    const [dismissing, setDismissing] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getInboundDocuments(status);
            setDocuments(res.data || []);
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('inbound_documents.failed_load')));
            setDocuments([]);
        } finally {
            setLoading(false);
        }
    }, [status, t]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const selected = useMemo(
        () => documents.find((d) => d.id === selectedId) || null,
        [documents, selectedId]
    );

    // Keep the selection on a real row: a refetch, or a status change, can
    // remove the document that was open (confirming one is exactly that).
    useEffect(() => {
        if (documents.length === 0) {
            setSelectedId(null);
            return;
        }
        setSelectedId((prev) =>
            prev && documents.some((d) => d.id === prev) ? prev : documents[0].id
        );
    }, [documents]);

    useEffect(() => {
        setAttachmentIndex(0);
    }, [selectedId]);

    const handleConfirm = () => {
        if (!selected) return;
        // Two calls, and the ORDER is the point: the invoice is created first,
        // through the real route with all its validation, and only then is
        // this row marked consumed. If the second call fails the row stays
        // pending and the same document is offered again — a duplicate draft
        // the user can see and delete, never a document that vanished
        // (Principle 2). SupplierInvoicesPage says so out loud if it happens.
        navigate('/dashboard/supplier-invoices', {
            state: { intake: buildIntakeHandoff(selected) },
        });
    };

    const handleDismissConfirmed = async () => {
        if (!dismissTarget) return;
        setDismissing(true);
        try {
            await dismissInboundDocument(dismissTarget.id);
            toast.success(t('inbound_documents.dismissed'));
            setDismissTarget(null);
            fetchDocuments();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('inbound_documents.failed_dismiss')));
        } finally {
            setDismissing(false);
        }
    };

    const handleOpenEml = async () => {
        if (!selected?.eml_path) return;
        try {
            const res = await getInboundDocumentFileBlob(selected.eml_path);
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${selected.id}.eml`;
            link.click();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('inbound_documents.failed_open_eml')));
        }
    };

    const attachments = selected?.file_paths || [];
    const attachmentNames = selected?.file_names || [];
    const activeAttachment = attachments[attachmentIndex] || null;

    return (
        <div className="space-y-6" data-testid="inbound-documents-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {t('inbound_documents.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('inbound_documents.subtitle')}</p>
                </div>
                <div className="flex items-end gap-2 flex-wrap">
                    <div className="flex flex-col gap-1">
                        <Label htmlFor="inbound-status-filter" className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {t('inbound_documents.filter_label')}
                        </Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger
                                id="inbound-status-filter"
                                className="w-[190px]"
                                data-testid="inbound-status-filter"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {t(`inbound_documents.status_${option}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button
                        variant="outline"
                        className="uppercase tracking-wider"
                        onClick={fetchDocuments}
                        disabled={loading}
                        data-testid="inbound-refresh-btn"
                    >
                        <RotateCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
                        {t('inbound_documents.refresh')}
                    </Button>
                </div>
            </div>

            {loading && documents.length === 0 ? (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
                </div>
            ) : documents.length === 0 ? (
                /* This screen is empty most days, and that is the GOOD state —
                   say so plainly rather than showing a hollow table. */
                <Card>
                    <CardContent className="py-16 flex flex-col items-center text-center gap-3">
                        <Inbox className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />
                        <p className="font-medium" data-testid="inbound-empty-state">
                            {status === 'pending'
                                ? t('inbound_documents.empty_pending')
                                : t('inbound_documents.empty_other')}
                        </p>
                        {status === 'pending' && (
                            <p className="text-sm text-muted-foreground max-w-md">
                                {t('inbound_documents.empty_hint')}
                            </p>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                    {/* The queue */}
                    <div className="lg:col-span-2 space-y-2" data-testid="inbound-document-list">
                        {documents.map((doc) => {
                            const abandoned = isAbandoned(doc);
                            const amount = doc.fields?.amount;
                            return (
                                <button
                                    key={doc.id}
                                    type="button"
                                    onClick={() => setSelectedId(doc.id)}
                                    aria-current={doc.id === selectedId}
                                    className={cn(
                                        'w-full text-left border border-border p-3 transition-colors hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                        doc.id === selectedId && 'bg-muted border-foreground'
                                    )}
                                    data-testid={`inbound-document-row-${doc.id}`}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-medium text-sm truncate">
                                            {doc.subject || t('inbound_documents.no_subject')}
                                        </p>
                                        {amount != null && (
                                            <span className="font-mono text-sm shrink-0">
                                                {formatNumber(Number(amount))}{' '}
                                                {doc.fields?.currency || ''}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {doc.sender_name || doc.sender_email || t('inbound_documents.unknown_sender')}
                                        {doc.received_at ? ` · ${formatDate(doc.received_at)}` : ''}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                        <Badge variant="outline" className="text-[10px]">
                                            {(doc.doc_type_guess || 'invoice') === 'receipt'
                                                ? t('supplier_invoices.doc_type_receipt')
                                                : t('supplier_invoices.doc_type_invoice')}
                                        </Badge>
                                        {doc.status !== 'pending' && (
                                            <Badge variant="secondary" className="text-[10px]">
                                                {t(`inbound_documents.status_${doc.status}`)}
                                            </Badge>
                                        )}
                                        {abandoned && (
                                            <Badge variant="destructive" className="text-[10px]">
                                                {t('inbound_documents.abandoned_badge')}
                                            </Badge>
                                        )}
                                        {doc.flagged_unknown_sender && (
                                            <Badge
                                                variant="outline"
                                                className="text-[10px] border-amber-500 text-amber-700"
                                                data-testid={`inbound-flag-${doc.id}`}
                                            >
                                                <AlertTriangle className="w-3 h-3 mr-1" />
                                                {t('inbound_documents.flag_badge')}
                                            </Badge>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* The document, beside what was read off it */}
                    {selected && (
                        <Card className="lg:col-span-3" data-testid="inbound-document-detail">
                            <CardContent className="p-4 space-y-4">
                                <div>
                                    <h2 className="font-heading text-xl font-bold">
                                        {selected.subject || t('inbound_documents.no_subject')}
                                    </h2>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {t('inbound_documents.from_sender', {
                                            sender:
                                                selected.sender_email ||
                                                t('inbound_documents.unknown_sender'),
                                        })}
                                        {selected.received_at
                                            ? ` · ${formatDate(selected.received_at)}`
                                            : ''}
                                    </p>
                                </div>

                                {/* An unknown sender AND no matching supplier — the
                                    shape of a fraudulent invoice. Visible and
                                    legible; never a block (KD-090b). */}
                                {selected.flagged_unknown_sender && (
                                    <div
                                        className="flex gap-3 border border-amber-500 bg-amber-50 dark:bg-amber-950/30 p-3"
                                        data-testid="inbound-flag-banner"
                                    >
                                        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium">
                                                {t('inbound_documents.flag_title')}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {t('inbound_documents.flag_body')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* An invoice that failed to parse is still an
                                    invoice that arrived. Silence here is the
                                    failure mode the whole phase exists to
                                    avoid (Principle 2). */}
                                {(selected.status === 'error' || selected.status === 'ignored') && (
                                    <div
                                        className="flex gap-3 border border-border bg-muted/40 p-3"
                                        data-testid="inbound-reason-banner"
                                    >
                                        <FileWarning className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium">
                                                {selected.status === 'error'
                                                    ? isAbandoned(selected)
                                                        ? t('inbound_documents.error_abandoned_title')
                                                        : t('inbound_documents.error_title')
                                                    : t('inbound_documents.ignored_title')}
                                            </p>
                                            <p className="text-sm text-muted-foreground">
                                                {selected.status === 'error'
                                                    ? t('inbound_documents.error_body')
                                                    : t('inbound_documents.ignored_body')}
                                            </p>
                                            {selected.reason && (
                                                <p className="text-xs font-mono text-muted-foreground mt-1 break-all">
                                                    {t('inbound_documents.reason_label')}:{' '}
                                                    {selected.reason}
                                                    {selected.attempts > 0 &&
                                                        ` · ${t('inbound_documents.attempts_label', { n: selected.attempts })}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* What was read */}
                                    <div className="space-y-3 min-w-0">
                                        {/* KD-093: Supplier Invoices is for payables only.
                                            A receipt was already paid by someone and
                                            belongs in their expense report. */}
                                        {selected.status === 'pending' &&
                                            selected.doc_type_guess === 'receipt' && (
                                                <div
                                                    className="flex gap-3 border border-border bg-muted/40 p-3"
                                                    data-testid="inbound-receipt-hint"
                                                >
                                                    <Receipt className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                                                    <p className="text-sm text-muted-foreground">
                                                        {t('inbound_documents.receipt_hint')}
                                                    </p>
                                                </div>
                                            )}

                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                                                {t('inbound_documents.read_fields')}
                                            </p>
                                            <dl className="text-sm divide-y divide-border border border-border">
                                                {FIELD_ROWS.map(([key, labelKey]) => {
                                                    const value = formatFieldValue(
                                                        key,
                                                        selected.fields?.[key]
                                                    );
                                                    return (
                                                        <div
                                                            key={key}
                                                            className="flex justify-between gap-3 px-3 py-1.5"
                                                        >
                                                            <dt className="text-muted-foreground">
                                                                {t(labelKey)}
                                                            </dt>
                                                            <dd
                                                                className={cn(
                                                                    'font-mono text-right break-all',
                                                                    value === null &&
                                                                        'text-muted-foreground italic font-sans'
                                                                )}
                                                            >
                                                                {value === null
                                                                    ? t('inbound_documents.not_read')
                                                                    : value}
                                                            </dd>
                                                        </div>
                                                    );
                                                })}
                                            </dl>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('inbound_documents.partial_fill_hint')}
                                            </p>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {selected.eml_path && (
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={handleOpenEml}
                                                    data-testid="inbound-open-eml"
                                                >
                                                    <Mail className="w-4 h-4 mr-2" />
                                                    {t('inbound_documents.open_eml')}
                                                </Button>
                                            )}
                                            {selected.mail_web_link && (
                                                <Button asChild variant="outline" size="sm">
                                                    <a
                                                        href={selected.mail_web_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        aria-label={t('inbound_documents.open_in_outlook_aria')}
                                                        data-testid="inbound-open-outlook"
                                                    >
                                                        <ExternalLink className="w-4 h-4 mr-2" aria-hidden="true" />
                                                        {t('inbound_documents.open_in_outlook')}
                                                    </a>
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* The document itself */}
                                    <div className="min-w-0">
                                        {attachments.length > 1 && (
                                            <div className="flex flex-wrap gap-1 mb-2">
                                                {attachments.map((path, index) => (
                                                    <Button
                                                        key={path}
                                                        type="button"
                                                        variant={
                                                            index === attachmentIndex
                                                                ? 'secondary'
                                                                : 'ghost'
                                                        }
                                                        size="sm"
                                                        onClick={() => setAttachmentIndex(index)}
                                                    >
                                                        {attachmentNames[index] ||
                                                            t('inbound_documents.attachment_n', {
                                                                n: index + 1,
                                                            })}
                                                    </Button>
                                                ))}
                                            </div>
                                        )}
                                        {activeAttachment ? (
                                            <SupplierInvoicePreview
                                                filePath={activeAttachment}
                                                fileName={attachmentNames[attachmentIndex] || null}
                                                // A held document has no
                                                // supplier_invoices row, so the
                                                // default endpoint proves no
                                                // ownership for this path and
                                                // 404s. Module-level reference:
                                                // the preview depends on it.
                                                fetchBlob={getInboundDocumentFileBlob}
                                                className="h-[420px]"
                                            />
                                        ) : (
                                            /* A body-only receipt. There is no
                                               attachment to render and the .eml
                                               is not something a browser can
                                               display — the fields beside this
                                               are what was read, and the .eml
                                               above is the record. */
                                            <div
                                                className="h-[420px] flex flex-col items-center justify-center gap-2 border border-border bg-muted/20 p-6 text-center"
                                                data-testid="inbound-no-attachment"
                                            >
                                                <Mail
                                                    className="w-8 h-8 text-muted-foreground"
                                                    strokeWidth={1.5}
                                                />
                                                <p className="text-sm font-medium">
                                                    {t('inbound_documents.no_attachment_title')}
                                                </p>
                                                <p className="text-sm text-muted-foreground max-w-xs">
                                                    {t('inbound_documents.no_attachment_body')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {selected.status === 'pending' && (
                                    <div className="flex justify-end gap-2 pt-2 border-t border-border">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className="uppercase tracking-wider"
                                            onClick={() => setDismissTarget(selected)}
                                            data-testid="inbound-dismiss-btn"
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            {t('inbound_documents.dismiss')}
                                        </Button>
                                        <Button
                                            type="button"
                                            className="uppercase tracking-wider"
                                            onClick={handleConfirm}
                                            data-testid="inbound-confirm-btn"
                                        >
                                            <Check className="w-4 h-4 mr-2" />
                                            {t('inbound_documents.confirm_as_invoice')}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            <AlertDialog
                open={!!dismissTarget}
                onOpenChange={(open) => !open && setDismissTarget(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('inbound_documents.dismiss_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('inbound_documents.dismiss_body')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={dismissing}>
                            {t('common.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(event) => {
                                event.preventDefault();
                                handleDismissConfirmed();
                            }}
                            disabled={dismissing}
                            data-testid="inbound-dismiss-confirm"
                        >
                            {dismissing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {t('inbound_documents.dismiss')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
